import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosInstance } from 'axios';
import { getNoticeExamples, getNoticeStyle, NoticeStyle, NoticeStyleSet, saveNoticeStyle } from '../../api/issues';
import { describeError } from '../../api/requeue';

type Part = Exclude<keyof NoticeStyle, 'codes'>;

const PARTS: { part: Part; label: string; rows: number }[] = [
    { part: 'subject', label: 'Onderwerp', rows: 1 },
    { part: 'greeting', label: 'Aanhef', rows: 1 },
    { part: 'intro', label: 'Inleiding (meer punten)', rows: 2 },
    { part: 'introOne', label: 'Inleiding (één punt)', rows: 2 },
    { part: 'concerns', label: 'Woord voor de facturen', rows: 1 },
    { part: 'outro', label: 'Afsluiting', rows: 2 },
    { part: 'signOff', label: 'Groet', rows: 1 },
    { part: 'signature', label: 'Ondertekening', rows: 3 },
];

/**
 * How we write to customers, per language: the parts around the list and our own wording per
 * kind of problem. Whatever is left empty uses the built-in text, shown greyed as the
 * placeholder. With the examples of what was really sent, as JSON for the c2e-customer-mail-style
 * skill to learn from.
 */
export const NoticeStylePanel = ({ axios }: { axios: AxiosInstance | undefined }) => {
    const [language, setLanguage] = useState<'nl' | 'en'>('nl');
    const [set, setSet] = useState<NoticeStyleSet | null>(null);
    const [style, setStyle] = useState<NoticeStyle>({ codes: {} });
    const [busy, setBusy] = useState<boolean>(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!axios) {
            return;
        }

        setSet(null);
        setError(null);

        getNoticeStyle(axios, language)
            .then((result) => {
                setSet(result);
                setStyle({ ...result.style, codes: { ...(result.style.codes ?? {}) } });
            })
            .catch((ex) => setError(describeError(ex)));
    }, [axios, language]);

    const save = () => {
        if (!axios) {
            return;
        }

        setBusy(true);
        setMessage(null);

        saveNoticeStyle(axios, language, style)
            .then(() => setMessage('Opgeslagen. De volgende mail gebruikt deze teksten.'))
            .catch((ex) => setError(describeError(ex)))
            .finally(() => setBusy(false));
    };

    const download = async () => {
        if (!axios) {
            return;
        }

        try {
            const examples = await getNoticeExamples(axios, 200);
            const data = { language, style, defaults: set?.defaults, examples };
            const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
            const link = document.createElement('a');

            link.href = url;
            link.download = `c2e-customer-mail-style-${new Date().toISOString().slice(0, 10)}.json`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (ex) {
            setError(describeError(ex));
        }
    };

    const importJson = () => {
        const text = window.prompt('Plak de huisstijl als JSON (zoals de skill c2e-customer-mail-style hem geeft):');

        if (!text) {
            return;
        }

        try {
            const parsed = JSON.parse(text) as NoticeStyle;
            setStyle({ ...parsed, codes: { ...(parsed.codes ?? {}) } });
            setMessage('Ingeladen; controleer de teksten en sla op.');
        } catch {
            setError('Dat is geen geldige JSON.');
        }
    };

    return (
        <details className="issues__notice">
            <summary className="issues__heading">
                <FontAwesomeIcon icon="envelope" /> Huisstijl van de klantmails
            </summary>

            <div className="issues__notice-head">
                <div className="issues__actions" role="group" aria-label="Taal">
                    {(['nl', 'en'] as const).map((code) => (
                        <button
                            key={code}
                            type="button"
                            className={`btn btn-sm ${language === code ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setLanguage(code)}
                        >
                            {code === 'nl' ? 'Nederlands' : 'English (ook voor de, fr, es, ...)'}
                        </button>
                    ))}
                </div>
                <div className="issues__actions">
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={download}>
                        <FontAwesomeIcon icon="file-code" /> Voorbeelden + huisstijl (JSON)
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={importJson}>
                        JSON inladen
                    </button>
                    <button type="button" className="btn btn-sm btn-primary" onClick={save} disabled={busy || !set}>
                        <FontAwesomeIcon icon="check" /> Opslaan
                    </button>
                </div>
            </div>

            <p className="issues__muted">
                Leeg laten is de standaardtekst (grijs). Te gebruiken: {'{voornaam}'}, {'{achternaam}'}, {'{klant}'}, {'{omgeving}'},{' '}
                {'{aantal}'}. Een aanhef zonder bekende voornaam laat de naam weg. Pas je een mail aan voordat je hem verstuurt, dan bewaart
                "Markeer als verstuurd" de echte tekst als voorbeeld; de skill <code>c2e-customer-mail-style</code> leidt daar de huisstijl uit af.
            </p>

            {error && <p className="issues__error">{error}</p>}
            {message && <p className="issues__muted">{message}</p>}

            {set && (
                <div className="notice-style">
                    {PARTS.map(({ part, label, rows }) => (
                        <label key={part} className="notice-style__field">
                            <span>{label}</span>
                            <textarea
                                className="form-control"
                                rows={rows}
                                value={(style[part] as string | null | undefined) ?? ''}
                                placeholder={(set.defaults[part] as string | null | undefined) ?? ''}
                                onChange={(e) => setStyle({ ...style, [part]: e.target.value })}
                            />
                        </label>
                    ))}

                    <h4 className="issues__heading">Per soort probleem</h4>
                    {Object.entries(set.defaults.codes ?? {}).map(([code, text]) => (
                        <div key={code} className="notice-style__code">
                            <code>{code}</code>
                            <input
                                className="form-control"
                                value={style.codes[code]?.title ?? ''}
                                placeholder={text.title ?? ''}
                                onChange={(e) => setStyle({ ...style, codes: { ...style.codes, [code]: { ...style.codes[code], title: e.target.value } } })}
                                aria-label={`Titel ${code}`}
                            />
                            <textarea
                                className="form-control"
                                rows={2}
                                value={style.codes[code]?.ask ?? ''}
                                placeholder={text.ask ?? ''}
                                onChange={(e) => setStyle({ ...style, codes: { ...style.codes, [code]: { ...style.codes[code], ask: e.target.value } } })}
                                aria-label={`Vraag ${code}`}
                            />
                        </div>
                    ))}
                </div>
            )}
        </details>
    );
};
